import {
  IFormThingsToImprove,
  InitiativeStatus,
} from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getBusinessBridgeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/cruds/getBusinessBridgeCrud'
import {
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
} from 'legacy/lut/components/widgets/public/partner360/planning/config'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useActualsData } from 'legacy/lut/features/partner360/configuration/hooks/useActualsData'
import { useCurrencyData } from 'legacy/lut/features/partner360/configuration/hooks/useCurrencyData'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import MeasureController from 'core/analytics/MeasureController'
import { KeyInitiative, useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'
import { useGetPlanningQuery } from 'partner360/entities/planning'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'
import { filterKeyInitiativeSettingsByYear } from 'partner360/features/keyInitiativeEditor/lib/filterKeyInitiativeSettingsByYear'
import { getKeyInitiativeProgramType } from 'partner360/features/keyInitiativeEditor/lib/getKeyInitiativeProgramType'
import { getOtherKeyInitiativesForPlan } from 'partner360/features/keyInitiativeEditor/lib/getOtherKeyInitiativesForPlan'
import { getKeyInitiativeTargetValueType } from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeFinancialTarget'
import {
  INITIATIVES_EDITOR_TYPE_LEGACY,
  INITIATIVES_EDITOR_TYPE_STEPS,
} from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { buildKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/lib/buildKeyInitiativesFinancialData'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'

import { getJbpBridgesCrud } from '../../../../commercialBridges/components/businessBridges/helpers/getJbpBridgesCrud'
import { applyKeyInitiativeChanges } from '../../../api/applyKeyInitiativeChanges'
import { useKeyInitiativesEditor } from '../../../hooks/useKeyInitiativesEditor'
import { IFormKIEditor } from '../../../models'
import {
  KEY_INITIATIVE_FORM_KEY,
  PARENT_KEY_INITIATIVE_FORM_KEY,
} from '../keyInitiativeEditorModal/configs'
import { EKeyInitiativeGoalMode } from '../keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { mapFormToKeyInitiative } from '../keyInitiativeEditorModal/helpers/mapFormToKeyInitiative'
import { mapKeyInitiativeToForm } from '../keyInitiativeEditorModal/helpers/mapKeyInitiativeToForm'
import { KeyInitiativeEditorModal } from '../keyInitiativeEditorModal/KeyInitiativeEditorModal'
import { KeyInitiativeStepsModal } from '../keyInitiativeEditorModal/KeyInitiativeStepsModal'
import { IKeyInitiativeForm } from '../keyInitiativeEditorModal/models/keyInitiativeForm'

import { prepareKeyInitiativeSave } from './prepareKeyInitiativeSave'

export interface IKeyInitiativePostSaveContext {
  shouldRefreshBridges: boolean
  savedInitiative: Partial<IFormThingsToImprove>
}

interface IKeyInitiativeEditorProps {
  initialState?: Partial<IFormKIEditor>
  isDialogOpened: boolean
  isDataInsightPresent: boolean
  metrics?: MeasureController
  isDataInsightTab?: boolean
  isAmplifyImpactInfoSection?: boolean
  keyObjectivesFilter?: string
  partnerIdOverride?: string
  fYearOverride?: number
  programTypeOverride?: ProgramTypes
  isCommercialOverride?: boolean
  keyInitiativesFormatOverride?: number
  saveAsNew?: boolean
  previousEntryId?: number
  /** Copy-list sessions finalize shared refreshes after their outer dialog closes. */
  deferPostSaveRefresh?: boolean
  /** Saved during this copy session but not refetched yet; used only for leftover plan. */
  additionalInitiativesForPlan?: readonly KeyInitiative[]
  /** Initial switch selection for a new initiative only; never inferred from AddToBridges. */
  newInitiativeGoalMode?: EKeyInitiativeGoalMode

  onLoadingStateChange?: (isLoading: boolean) => void
  onEditCancel: () => void
  onUpdateFinished: (
    newData: IFormThingsToImprove[],
    context?: IKeyInitiativePostSaveContext
  ) => void | Promise<void>
}

export const KeyInitiativeEditor: FC<IKeyInitiativeEditorProps> = ({
  initialState,
  isDialogOpened,
  isDataInsightPresent,
  isDataInsightTab,
  isAmplifyImpactInfoSection,
  partnerIdOverride,
  fYearOverride,
  programTypeOverride,
  isCommercialOverride,
  keyInitiativesFormatOverride,
  saveAsNew = false,
  previousEntryId,
  deferPostSaveRefresh = false,
  additionalInitiativesForPlan,
  newInitiativeGoalMode,

  onLoadingStateChange,
  onEditCancel,
  onUpdateFinished,
}) => {
  /** -------------------- State ----------------------- */

  const { isGlobal, isRegionalDistributor } = useSelector(selectPartnerAccess)
  const [keyInitiativesFormat, setKeyInitiativesFormat] = useState(
    keyInitiativesFormatOverride ??
      (isGlobal ? INITIATIVES_EDITOR_TYPE_LEGACY : INITIATIVES_EDITOR_TYPE_STEPS)
  )
  const persistedEntryIdRef = useRef(saveAsNew ? undefined : initialState?.EntryId)

  /** -------------------- Selectors ----------------------- */

  /** -------------------- Variables ----------------------- */

  const normalizedKeyInitiativesFormat = useMemo(() => {
    if (typeof keyInitiativesFormatOverride === 'number') {
      return keyInitiativesFormatOverride >= INITIATIVES_EDITOR_TYPE_STEPS
        ? INITIATIVES_EDITOR_TYPE_STEPS
        : INITIATIVES_EDITOR_TYPE_LEGACY
    }

    return isGlobal ? INITIATIVES_EDITOR_TYPE_LEGACY : INITIATIVES_EDITOR_TYPE_STEPS
  }, [isGlobal, keyInitiativesFormatOverride])

  const isNewKeyInitiativesFormat = useMemo(() => {
    return keyInitiativesFormat >= INITIATIVES_EDITOR_TYPE_STEPS
  }, [keyInitiativesFormat])

  /** -------------------- Hooks ----------------------- */

  const { isCommercial, recalculateCompleteness, ownerId, fYear, programType, partnerId } =
    useKeyInitiativesEditor({
      entryId: saveAsNew ? undefined : initialState?.EntryId,
      partnerId: partnerIdOverride,
      fYear: fYearOverride,
      programType: programTypeOverride,
      isCommercial: isCommercialOverride,
    })
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const [isApplying, setIsApplying] = useState(false)

  useHoldPlanSectionOpen(isApplying)

  const {
    data: planningData,
    isLoading: isFinDataLoading,
    isFetching: isFinDataFetching,
  } = useGetPlanningQuery(
    {
      partnerId,
      fYear,
      programType,
      fromFYear: Math.max(fYear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR),
      isRegionalDistributor,
    },
    {
      skip: !isDialogOpened || !partnerId || !fYear || !programType,
    }
  )

  const finSobData = useMemo(
    () => buildKeyInitiativesFinancialData(planningData?.Planning ?? [], fYear, false),
    [planningData?.Planning, fYear]
  )

  // Reuse the unfiltered current-year query/cache, not the table's period or display filters.
  const { currentData: currentYearInitiatives } = useGetKeyInitiativesQuery(
    { partnerId, fYear, programType },
    { skip: !isDialogOpened || !isNewKeyInitiativesFormat || !partnerId || !fYear || !programType }
  )
  const otherInitiatives = useMemo(
    () =>
      getOtherKeyInitiativesForPlan(
        [
          ...(currentYearInitiatives ?? []),
          ...(additionalInitiativesForPlan ?? []).filter(
            (copy) =>
              !currentYearInitiatives?.some(
                (item) =>
                  item.PreviousEntryId === copy.PreviousEntryId &&
                  item.PartnerId === copy.PartnerId &&
                  item.FYear === copy.FYear
              )
          ),
        ],
        {
          partnerId,
          fYear,
          programType,
          entryId: saveAsNew ? undefined : initialState?.EntryId,
        }
      ),
    [
      currentYearInitiatives,
      additionalInitiativesForPlan,
      partnerId,
      fYear,
      programType,
      saveAsNew,
      initialState?.EntryId,
    ]
  )

  const { jbpSettings } = useJbpSettings()
  const { actualsValueType } = useActualsData()
  const { currency } = useCurrencyData()
  const businessPriorities = jbpSettings.kiBusinessPriorities
  const keyObjectives = useMemo(
    () => filterKeyInitiativeSettingsByYear(jbpSettings.keyObjectives, fYear),
    [fYear, jbpSettings.keyObjectives]
  )
  const keyObjectiveTags = useMemo(
    () => filterKeyInitiativeSettingsByYear(jbpSettings.keyObjectiveTags, fYear),
    [fYear, jbpSettings.keyObjectiveTags]
  )

  const mappedInitialState = useMemo(() => {
    const mapped = mapKeyInitiativeToForm(
      {
        ...initialState,
        Currency: initialState?.Currency || currency,
        ValueType: getKeyInitiativeTargetValueType(initialState?.ValueType, actualsValueType),
      },
      businessPriorities,
      keyObjectives,
      newInitiativeGoalMode
    )
    return saveAsNew
      ? {
          ...mapped,
          entryId: undefined,
          previousEntryId: previousEntryId ?? null,
          status: InitiativeStatus.New,
        }
      : mapped
  }, [
    actualsValueType,
    businessPriorities,
    currency,
    initialState,
    keyObjectives,
    newInitiativeGoalMode,
    previousEntryId,
    saveAsNew,
  ])

  /** -------------------- Methods ----------------------- */

  const handleBusyStateChange = useCallback(
    (isBusy: boolean) => {
      setIsApplying(isBusy)
      onLoadingStateChange?.(isBusy)
    },
    [onLoadingStateChange]
  )

  const handleSetKeyInitiativesFormat = useCallback((newFormat: number) => {
    setKeyInitiativesFormat(
      newFormat >= INITIATIVES_EDITOR_TYPE_STEPS
        ? INITIATIVES_EDITOR_TYPE_STEPS
        : INITIATIVES_EDITOR_TYPE_LEGACY
    )
  }, [])

  const applyChanges = useCallback(
    async (
      mappedData: Partial<IFormThingsToImprove>,
      action: 'Create' | 'Update',
      keyObjective: string
    ) => {
      const initiativeProgramType = getKeyInitiativeProgramType(
        keyObjective,
        keyObjectives,
        programType
      )
      return await applyKeyInitiativeChanges<IFormThingsToImprove>({
        ownerId,
        fYear,
        programType: initiativeProgramType,
        partnerId,
        // Update must explicitly clear the stored offering, not just pass a null procedure argument.
        data: { ...mappedData, ProgramType: initiativeProgramType },
        action,
      })
    },
    [ownerId, fYear, programType, partnerId, keyObjectives]
  )

  const handleApply = useCallback(
    async (form: IKeyInitiativeForm, isInvestmentNeeds: boolean) => {
      handleBusyStateChange(true)

      try {
        const formData = mapFormToKeyInitiative(
          form,
          isInvestmentNeeds,
          businessPriorities,
          keyObjectives
        )
        const mappedData = saveAsNew
          ? {
              Comment: initialState?.Comment,
              Category: initialState?.Category,
              Products: initialState?.Products,
              IsFromGlobalPartner: initialState?.IsFromGlobalPartner,
              IsSwot: initialState?.IsSwot,
              KeyObjectiveCategory: initialState?.KeyObjectiveCategory,
              FocusAreaTranslation: initialState?.FocusAreaTranslation,
              ...formData,
            }
          : formData
        const prepared = prepareKeyInitiativeSave(mappedData, {
          saveAsNew,
          previousEntryId,
          persistedEntryId: persistedEntryIdRef.current,
        })
        const data = await applyChanges(prepared.data, prepared.action, form.keyObjective)
        if (saveAsNew && !data) {
          return
        }
        const savedEntryId = prepared.data.EntryId ?? data?.[0]?.EntryId

        if (savedEntryId) {
          persistedEntryIdRef.current = savedEntryId
        }

        const shouldRefreshBridges = Boolean(
          form.shouldAddToBridges || mappedInitialState?.shouldAddToBridges
        )
        if (!saveAsNew || !deferPostSaveRefresh) {
          // Cockpit also writes Completeness: reload it only after the calculation finishes.
          await recalculateCompleteness()
          refetchPlanningCockpitData()
          if (shouldRefreshBridges) {
            getBusinessBridgeCrud().removeQueries(true)
            getJbpBridgesCrud().removeQueries(true)
          }
        }
        await onUpdateFinished(data ?? [], {
          shouldRefreshBridges,
          savedInitiative: {
            ...prepared.data,
            EntryId: savedEntryId,
            PartnerId: partnerId,
            FYear: fYear,
            ProgramType: getKeyInitiativeProgramType(form.keyObjective, keyObjectives, programType),
          },
        })
      } finally {
        handleBusyStateChange(false)
      }
    },
    [
      mappedInitialState?.shouldAddToBridges,
      businessPriorities,
      keyObjectives,
      handleBusyStateChange,
      applyChanges,
      onUpdateFinished,
      recalculateCompleteness,
      refetchPlanningCockpitData,
      deferPostSaveRefresh,
      fYear,
      partnerId,
      programType,
      saveAsNew,
      previousEntryId,
      initialState,
    ]
  )

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    onLoadingStateChange?.(isApplying || isFinDataLoading || isFinDataFetching)
  }, [isApplying, isFinDataLoading, isFinDataFetching, onLoadingStateChange])

  useEffect(() => {
    setKeyInitiativesFormat(normalizedKeyInitiativesFormat)
  }, [normalizedKeyInitiativesFormat])

  useEffect(() => {
    if (isDialogOpened) {
      persistedEntryIdRef.current = saveAsNew ? undefined : initialState?.EntryId
    } else {
      persistedEntryIdRef.current = undefined
    }
  }, [initialState?.EntryId, isDialogOpened, saveAsNew])

  /** -------------------- Render ----------------------- */

  return (
    <>
      {!isNewKeyInitiativesFormat && (
        <KeyInitiativeEditorModal
          initialData={mappedInitialState}
          isOpen={isDialogOpened}
          isDataInsightPresent={isDataInsightPresent}
          isDataInsightTab={isDataInsightTab}
          isAmplifyImpactInfoSection={isAmplifyImpactInfoSection}
          partnerId={partnerId}
          programType={programType}
          onApply={handleApply}
          onCancel={onEditCancel}
          saveAsNew={saveAsNew}
          isCommercial={isCommercial}
          pendingKeyInitiative={!saveAsNew && initialState?.IsNotApplicable === null}
          finSobData={finSobData}
          keyInitiativesFormat={keyInitiativesFormat}
          setKeyInitiativesFormat={handleSetKeyInitiativesFormat}
          fYear={fYear}
          businessPriorities={businessPriorities}
          keyObjectives={keyObjectives}
          keyObjectiveTags={keyObjectiveTags}
          initiativeFormKey={KEY_INITIATIVE_FORM_KEY}
          parentFormKey={PARENT_KEY_INITIATIVE_FORM_KEY}
        />
      )}
      {isNewKeyInitiativesFormat && (
        <KeyInitiativeStepsModal
          initialData={mappedInitialState}
          partnerId={partnerId}
          isOpen={isDialogOpened}
          isSaving={isApplying}
          onBusyStateChange={handleBusyStateChange}
          isDataInsightPresent={isDataInsightPresent}
          isDataInsightTab={isDataInsightTab}
          isAmplifyImpactInfoSection={isAmplifyImpactInfoSection}
          programType={programType}
          onApply={handleApply}
          onCancel={onEditCancel}
          saveAsNew={saveAsNew}
          isCommercial={isCommercial}
          pendingKeyInitiative={!saveAsNew && initialState?.IsNotApplicable === null}
          finSobData={finSobData}
          otherInitiatives={otherInitiatives}
          keyInitiativesFormat={keyInitiativesFormat}
          setKeyInitiativesFormat={handleSetKeyInitiativesFormat}
          fYear={fYear}
          businessPriorities={businessPriorities}
          keyObjectives={keyObjectives}
          keyObjectiveTags={keyObjectiveTags}
          initiativeFormKey={KEY_INITIATIVE_FORM_KEY}
          parentFormKey={PARENT_KEY_INITIATIVE_FORM_KEY}
        />
      )}
    </>
  )
}
